// src/routes/avatar-editor/constants.ts

import type { TFunction } from 'i18next';

import type { StepId, WizardState } from './types';

export const LOCAL_STORAGE_KEY = 'qlash:avatar-editor:draft:v1';

const FROM_PROMPT_DOTS: ReadonlyArray<{ id: StepId }> = [
  { id: 'concept' },
  { id: 'renderSetup' },
  { id: 'generate' },
  { id: 'motion' },
  { id: 'engine' },
];

const FROM_EXISTING_DOTS: ReadonlyArray<{ id: StepId }> = [
  { id: 'concept' },
  { id: 'motion' },
  { id: 'engine' },
];

/** Visible dots for the progress bar, gated by the wizard's source mode. */
export function stepDotsFor(sourceMode: WizardState['sourceMode']): ReadonlyArray<{ id: StepId }> {
  return sourceMode === 'fromExisting' ? FROM_EXISTING_DOTS : FROM_PROMPT_DOTS;
}

/**
 * Il nome di un passo, nella lingua dell'app. Le etichette NON stanno più nella tabella dei
 * puntini: un `.ts` non rende niente e non conosce la lingua, quindi qui si prende il `t` del
 * namespace `avatars` e ogni chiave è scritta per esteso (così `verify-lingue` la controlla).
 * Il primo passo si chiama diversamente a seconda di dove parte il wizard.
 */
export function stepLabel(t: TFunction, id: StepId, sourceMode: WizardState['sourceMode']): string {
  switch (id) {
    case 'concept': return sourceMode === 'fromExisting' ? t('editor.steps.source') : t('editor.steps.concept');
    case 'renderSetup': return t('editor.steps.render');
    case 'generate': return t('editor.steps.image');
    case 'motion': return t('editor.steps.motion');
    case 'engine': return t('editor.steps.engine');
    case 'confirm': return t('editor.steps.confirm');
  }
}

/** Full linear order for the fromPrompt path (renderSetup + generate present). */
const ORDER_FROM_PROMPT: ReadonlyArray<StepId> = [
  'concept', 'renderSetup', 'generate', 'motion', 'engine', 'confirm',
];

/** Shorter order when reusing an existing avatar — skip renderSetup + generate. */
const ORDER_FROM_EXISTING: ReadonlyArray<StepId> = [
  'concept', 'motion', 'engine', 'confirm',
];

function orderFor(sourceMode: WizardState['sourceMode']): ReadonlyArray<StepId> {
  return sourceMode === 'fromExisting' ? ORDER_FROM_EXISTING : ORDER_FROM_PROMPT;
}

export function nextStep(current: StepId, sourceMode: WizardState['sourceMode']): StepId | null {
  const order = orderFor(sourceMode);
  const idx = order.indexOf(current);
  if (idx < 0 || idx === order.length - 1) return null;
  return order[idx + 1];
}

export function prevStep(current: StepId, sourceMode: WizardState['sourceMode']): StepId | null {
  const order = orderFor(sourceMode);
  const idx = order.indexOf(current);
  if (idx <= 0) return null;
  return order[idx - 1];
}

/** Default initial state when the user starts a fresh wizard. */
export function makeInitialState(): WizardState {
  return {
    version: 1,
    startedAt: new Date().toISOString(),
    currentStep: 'concept',
    sourceMode: 'fromPrompt',
    title: '',
    prompt: '',
    aspectRatio: '1:1',
    transparent: true,
    branch3d: { enabled: false },
    motionPrompt: '',
    videoProvider: 'hailuo-fal',
    durationSec: 6,
    resolution: '512p',
    variants: 1,
    // Default to true alpha (matting) — produces dual-stream H.264 with
    // a real alpha mask, no magenta-edge artifacts on the silhouette. The
    // chroma-key fallback stays selectable in Step 5 for cheaper runs.
    videoTransparency: 'matting',
    videoSeed: '',
    // Default ON: a freshly generated wizard run is almost always something
    // the user wants to see on the Hub. They can flip the toggle off on Step
    // 6 before publishing if they prefer not to feature it.
    setAsFeatured: true,
  };
}
